// Currency formatting for revenue analytics. Driven by the PROJECT's base
// currency (ISO-4217), never a hard-coded 'USD'. V1 does no FX conversion —
// amounts are stored + summed in their original currency and displayed in the
// project's base currency label (the locked product decision; see the PRD).
//
// `formatCurrency` mirrors the old hard-coded formatter's shape (no fraction
// digits — revenue tiles read cleaner as whole units) but takes the currency
// code so every call site can pass the active project's `baseCurrency`.

import { DEFAULT_BASE_CURRENCY } from '@logspot-platform/platform-types';

/**
 * Format a monetary amount (major units) in the given ISO-4217 currency.
 * Falls back to the default base currency when the code is empty/invalid so a
 * misconfigured project still renders a number rather than throwing.
 *
 * @param value major-unit amount (e.g. 1234.5 for $1,234.50)
 * @param currency ISO-4217 code from the project's baseCurrency (default USD)
 * @param options.maximumFractionDigits decimals to show (default 0)
 */
export const formatCurrency = (
  value: number | null | undefined,
  currency: string | null | undefined,
  options?: { maximumFractionDigits?: number },
): string => {
  const code = (currency || DEFAULT_BASE_CURRENCY).toUpperCase();
  const amount = Number.isFinite(value as number) ? (value as number) : 0;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: options?.maximumFractionDigits ?? 0,
    }).format(amount);
  } catch {
    // Intl throws on an unknown currency code — degrade to the default.
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: DEFAULT_BASE_CURRENCY,
      maximumFractionDigits: options?.maximumFractionDigits ?? 0,
    }).format(amount);
  }
};
